// filepath: c:\Users\evict\Desktop\SpellgatherV2\web\src\tour\steps.ts
// Tour steps: add gentle timeouts where pages/data mount slowly,
// keep all existing stops, and make Bob the final step.

import React from "react";
import type { Step } from "react-joyride";

export type TourStep = {
  id: string;
  selector: string;
  title: string | React.ReactNode;
  body: string | React.ReactNode;
  route: string;  // target pathname
  placement?: "auto" | "top" | "bottom" | "left" | "right" | "center";
  padding?: number;
  spotlightRadius?: number;
  timeoutMs?: number; // optional per-step wait override
};

// NOTE: The TourProvider treats `selector: "body"` as a center modal step.
// Added timeoutMs on early dashboard/trades steps to accommodate auth+data loads.
// Reordered so the tour ends after introducing Bob (no steps removed).

export const TOUR_STEPS: Array<{
  id: string;
  route: string;
  selector: string;
  title: React.ReactNode;
  body: React.ReactNode;
  placement?: Step["placement"];
  timeoutMs?: number;
}> = [
  // Welcome: lands you on /dashboard first in your flow
  {
    id: "welcome",
    selector: "body",
    title: "Welcome",
    body: "Quick walkthrough of core areas. You can exit anytime.",
    route: "/dashboard",
    placement: "center",
    timeoutMs: 8000, // give dashboard a moment to auth/mount on first entry
  },

  // Dashboard
  {
    id: "dashboard-header",
    selector: '[data-tour="dashboard-header"]',
    title: "Dashboard",
    body: "Your personalized hub: stats, activity & tools.",
    route: "/dashboard",
    placement: "bottom",
    timeoutMs: 8000,
  },
  {
    id: "dashboard-stats",
    selector: '[data-tour="dashboard-stats"]',
    title: "Stats",
    body: "Snapshot of your collection & listings.",
    route: "/dashboard",
    placement: "top",
  },
  {
    id: "dashboard-followed",
    selector: '[data-tour="dashboard-followed"]',
    title: "Followed Activity",
    body: "Recent listings from people you follow.",
    route: "/dashboard",
    placement: "top",
  },
  {
    id: "dashboard-nearby",
    selector: '[data-tour="dashboard-nearby"]',
    title: "Nearby Activity",
    body: "Cards listed within your trade radius.",
    route: "/dashboard",
    placement: "top",
  },
  {
    id: "dashboard-alerts",
    selector: '[data-tour="dashboard-alerts"]',
    title: "Alerts",
    body: "Matches from your Wantlist that appeared on the marketplace.",
    route: "/dashboard",
    placement: "top",
  },
  {
    id: "dashboard-highlights",
    selector: '[data-tour="dashboard-highlights"]',
    title: "Highlights",
    body: "Interesting stats about your own collection.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "dashboard-playopps",
    selector: '[data-tour="dashboard-playopps"]',
    title: "Play Opportunities",
    body: "Players nearby sharing your preferred formats.",
    route: "/dashboard",
    placement: "left",
  },

  // Marketplace
  {
    id: "marketplace-page",
    selector: '[data-tour="marketplace-page"]',
    title: "Marketplace",
    body: "Browse community listings for sale and trade.",
    route: "/marketplace",
    placement: "center",
    timeoutMs: 4000,
  },
  {
    id: "marketplace-search",
    selector: '[data-tour="marketplace-search"]',
    title: "Search & Filter",
    body: "Search by card or user; combine with price & sort controls.",
    route: "/marketplace",
    placement: "bottom",
  },
  {
    id: "marketplace-filters",
    selector: '[data-tour="marketplace-filters"]',
    title: "Filters",
    body: "Fine-tune listings: price range & sorting.",
    route: "/marketplace",
    placement: "left",
  },
  {
    id: "marketplace-community",
    selector: '[data-tour="marketplace-community"]',
    title: "Community Panel",
    body: "Nearby traders marked available for trading/play.",
    route: "/marketplace",
    placement: "top",
  },
  {
    id: "marketplace-listings",
    selector: '[data-tour="marketplace-listings"]',
    title: "Listings Grid",
    body: "Hover or tap a card for quick seller actions & offers.",
    route: "/marketplace",
    placement: "top",
  },

  // Collection
  {
    id: "collection-page",
    selector: '[data-tour="collection-page"]',
    title: "My Collection",
    body: "Manage, search, scan & import your owned cards.",
    route: "/collection",
    placement: "center",
    timeoutMs: 4000,
  },
  {
    id: "collection-search",
    selector: '[data-tour="collection-search"]',
    title: "Search",
    body: "Live filter by name, set, or number.",
    route: "/collection",
    placement: "bottom",
  },
  {
    id: "collection-status-filter",
    selector: '[data-tour="collection-status-filter"]',
    title: "Status Filter",
    body: "Show only trade, sale, or keep cards.",
    route: "/collection",
    placement: "right",
  },
  {
    id: "collection-import",
    selector: '[data-tour="collection-import"]',
    title: "Bulk Import",
    body: "Paste decklists or CSV to add cards in bulk.",
    route: "/collection",
    placement: "bottom",
  },
  {
    id: "collection-scan",
    selector: '[data-tour="collection-scan"]',
    title: "Scanner",
    body: "OCR card images to speed up adding cards.",
    route: "/collection",
    placement: "bottom",
  },
  {
    id: "collection-grid",
    selector: '[data-tour="collection-grid"]',
    title: "Collection Grid",
    body: "Drag cards to binders, edit or list for trade/sale.",
    route: "/collection",
    placement: "top",
  },
  {
    id: "collection-wishlist",
    selector: '[data-tour="collection-wishlist"]',
    title: "Wishlist",
    body: "Track wants; alerts will appear when matches list.",
    route: "/collection",
    placement: "top",
  },

  // Trades (slow Firestore → give these more time)
  {
    id: "trades-page",
    selector: '[data-tour="trades-page"]',
    title: "Trades Workspace",
    body: "Build trade proposals & manage negotiations.",
    route: "/trades",
    placement: "center",
    timeoutMs: 8000,
  },
  {
    id: "trades-inbox",
    selector: '[data-tour="trades-inbox"]',
    title: "Trade Inbox",
    body: "Incoming & outgoing proposals.",
    route: "/trades",
    placement: "bottom",
    timeoutMs: 8000,
  },
  {
    id: "trades-sent",
    selector: '[data-tour="trades-sent"]',
    title: "Offering",
    body: "Cards you are offering.",
    route: "/trades",
    placement: "top",
    timeoutMs: 8000,
  },
  {
    id: "trades-received",
    selector: '[data-tour="trades-received"]',
    title: "Receiving",
    body: "Cards you would receive.",
    route: "/trades",
    placement: "top",
    timeoutMs: 8000,
  },
  {
    id: "trades-send-proposal",
    selector: '[data-tour="trades-send-proposal"]',
    title: "Send Proposal",
    body: "Submit and start negotiation.",
    route: "/trades",
    placement: "left",
    timeoutMs: 8000,
  },
  {
    id: "trades-footer",
    selector: '[data-tour="trades-footer"]',
    title: "Value Summary",
    body: "Live comparison of both sides.",
    route: "/trades",
    placement: "top",
    timeoutMs: 8000,
  },

  // Global Search
  {
    id: "global-search-section",
    selector: '[data-tour="global-search-section"]',
    title: "Global Card Search",
    body: "Search Scryfall & community listings together.",
    route: "/search",
    placement: "center",
    timeoutMs: 4000,
  },
  {
    id: "global-search-box",
    selector: '[data-tour="global-search-box"]',
    title: "Search Input",
    body: "Enter advanced or simple queries (Scryfall syntax).",
    route: "/search",
    placement: "bottom",
  },
  {
    id: "global-marketplace-section",
    selector: '[data-tour="global-marketplace-section"]',
    title: "Marketplace Matches",
    body: "Community listings matching your search.",
    route: "/search",
    placement: "top",
  },

  // Avatar Menu (ids start with "avatar-" so the provider auto-opens the menu)
  {
    id: "avatar-menu-trigger",
    selector: '[data-tour="avatar-menu-trigger"]',
    title: "Profile Menu",
    body: "Open your profile & quick actions.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-header",
    selector: '[data-tour="avatar-header"]',
    title: "Profile Snapshot",
    body: "Your rank, avatar, and identity summary.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-share-profile",
    selector: '[data-tour="avatar-share-profile"]',
    title: "Share Profile",
    body: "Copy or share your public profile link.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-trade-toggle",
    selector: '[data-tour="avatar-trade-toggle"]',
    title: "Trade Availability",
    body: "Toggle visibility for trading in marketplace.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-play-toggle",
    selector: '[data-tour="avatar-play-toggle"]',
    title: "Play Availability",
    body: "Toggle appearing in play/community streams.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-bug-report",
    selector: '[data-tour="avatar-bug-report"]',
    title: "Report Issues",
    body: "Quickly report a bug or open guided form.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-news-button",
    selector: '[data-tour="avatar-news-button"]',
    title: "What’s New",
    body: "See latest updates & release notes.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-tour-button",
    selector: '[data-tour="avatar-tour-button"]',
    title: "Restart Tour",
    body: "You can relaunch this tour anytime.",
    route: "/dashboard",
    placement: "left",
  },
  {
    id: "avatar-legal-toggle",
    selector: '[data-tour="avatar-legal-toggle"]',
    title: "Legal & Policies",
    body: "Review terms, privacy & community guidelines.",
    route: "/dashboard",
    placement: "left",
  },

    // Finish card (kept, but moved BEFORE Bob so the tour ENDS with Bob)
  {
    id: "finish",
    selector: "body",
    title: "All Set",
    body: "Tour complete. Restart from the avatar menu when needed.",
    route: "/dashboard",
    placement: "center",
  },

  // Bob — final step
  {
    id: "bob",
    selector: '[data-tour="bob-mascot"]',
    title: "Bob",
    body:
      "This is Bob. He works hard to keep everything running smoothly at Spellgather. We like Bob.",
    route: "/dashboard",
    placement: "left",
    timeoutMs: 8000, // allow time for header/layout to mount
  },
];

// Publish to window so the provider can consume without tight coupling
try {
  (window as any).__TOUR_STEPS = TOUR_STEPS;
} catch {}

